import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Col, Form, Row } from 'react-bootstrap';

// layout
import AppLayout from '../../../layouts/AppLayout/AppLayout';

// components
import { NioButton, NioToaster, NioCard, NioField, NioSection } from '../../../components';
import api from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';

function PostRequest() {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [searchParams] = useSearchParams();
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [services, setServices] = useState([]);
  const [availableInterventionCities, setAvailableInterventionCities] = useState([]);
  const [selectedInterventionRegion, setSelectedInterventionRegion] = useState('');
  const [portfolioPhotoFiles, setPortfolioPhotoFiles] = useState([]);
  const [portfolioVideoFiles, setPortfolioVideoFiles] = useState([]);
  const [portfolioProjectFiles, setPortfolioProjectFiles] = useState([]);
  const preSelectedTrade = searchParams.get('trade');
  const providerName = searchParams.get('providerName');
  const providerBusiness = searchParams.get('providerBusiness');
  const providerEmail = searchParams.get('providerEmail');
  const hasProviderParams =
    searchParams.has('providerName') ||
    searchParams.has('providerBusiness') ||
    searchParams.has('providerEmail');

  const DAYS = [
    { key: 'lun', label: t('postRequest.days.lun') },
    { key: 'mar', label: t('postRequest.days.mar') },
    { key: 'mer', label: t('postRequest.days.mer') },
    { key: 'jeu', label: t('postRequest.days.jeu') },
    { key: 'ven', label: t('postRequest.days.ven') },
    { key: 'sam', label: t('postRequest.days.sam') },
    { key: 'dim', label: t('postRequest.days.dim') }
  ];
  const tunisianRegions = {
    Ariana: ['Ariana', 'Raoued', 'Kalâat el-Andalous', 'Sidi Thabet', 'Ettadhamen', 'Mnihla'],
    'Béja': ['Béja', 'Medjez el-Bab', 'Testour', 'Nefza', 'Amdoun', 'Téboursouk'],
    'Ben Arous': ['Ben Arous', 'Rades', 'Mégrine', 'Hammam Lif', 'Fouchana', 'Ezzahra', 'Mohamedia'],
    Bizerte: ['Bizerte', 'Menzel Bourguiba', 'Mateur', 'Sejnane', 'Ghar El Melh', 'Ras Jebel'],
    'Gabès': ['Gabès', 'Médenine', 'Matmata', 'El Hamma', 'Ghannouch', 'Mareth'],
    Gafsa: ['Gafsa', 'El Ksar', 'Métlaoui', 'Redeyef', 'Moularès', 'Sidi Aïch'],
    Jendouba: ['Jendouba', 'Tabarka', 'Aïn Draham', 'Fernana', 'Bou Salem', 'Ghardimaou'],
    Kairouan: ['Kairouan', 'Sbikha', 'Haffouz', 'Chebika', 'Oueslatia', 'Nasrallah'],
    Kasserine: ['Kasserine', 'Sbeitla', 'Fériana', 'Thala', 'Haïdra', 'Jedelienne'],
    'Kébili': ['Kébili', 'Douz', 'Souk Lahad', 'Faouar', 'El Golâa'],
    Kef: ['Le Kef', 'Tajerouine', 'Kalâat Khasba', 'Dahmani', 'Sers', 'Nebeur'],
    Mahdia: ['Mahdia', 'Bou Merdes', 'Chebba', 'El Jem', 'Ksour Essef', 'Sidi Alouane'],
    Manouba: ['Manouba', 'Douar Hicher', 'Oued Ellil', 'Mornag', 'Borj El Amri', 'Djedeida'],
    'Médenine': ['Médenine', 'Tataouine', 'Ben Gardane', 'Zarzis', 'Djerba', 'Houmt Souk'],
    Monastir: ['Monastir', 'Moknine', 'Ksibet el-Médiouni', 'Bembla', 'Téboulba', 'Sayada'],
    Nabeul: ['Nabeul', 'Hammamet', 'Kélibia', 'Korba', 'Menzel Temime', 'Dar Chaabane', 'Béni Khalled'],
    Sfax: ['Sfax', 'Sakiet Ezzit', 'El Hencha', 'Agareb', 'Menzel Chaker', 'Bir Ali Ben Khalifa'],
    'Sidi Bouzid': ['Sidi Bouzid', 'Meknassy', 'Regueb', 'Ouled Haffouz', 'Cebbala Ouled Asker'],
    Siliana: ['Siliana', 'Bou Arada', 'Gaâfour', 'El Krib', 'Makthar', 'Rouhia'],
    Sousse: ['Sousse', 'Hammam Sousse', 'Akouda', 'Enfidha', 'Kalaa Kebira', 'Msaken', 'Bouficha'],
    Tataouine: ['Tataouine', 'Remada', 'Bir Lahmar', 'Ghomrassen', 'Dehiba'],
    Tozeur: ['Tozeur', 'Nefta', 'Degache', 'Hazoua', 'Tamaghza'],
    Tunis: ['Tunis', 'La Marsa', 'Carthage', 'Sidi Bou Said', 'Le Bardo', 'La Goulette'],
    Zaghouan: ['Zaghouan', 'Zriba', 'Nadhour', 'Fahs', 'Saouaf', 'El Fahs']
  };
  const sortedRegions = Object.keys(tunisianRegions).sort();
  const defaultDaySchedule = { open: false, start: '09:00', end: '18:00' };
  const [workSchedule, setWorkSchedule] = useState(() =>
    DAYS.reduce((acc, d) => ({ ...acc, [d.key]: { ...defaultDaySchedule } }), {})
  );

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    mode: 'onBlur',
    defaultValues: {
      trade: '',
      subService: '',
      scheduleNotes: '',
      about: '',
      experience: '',
      clientCommitments: '',
      paymentMethod: '',
      coverageGuarantees: '',
      servicesOffered: '',
      detailedSubServices: '',
      interventionCity: '',
      interventionRegion: '',
      experienceYears: '',
      projectsCompleted: '',
      previousCompanies: '',
      hourlyPrice: '',
      servicePrice: '',
      customQuote: '',
      urgentAvailability: '',
      averageRating: '',
      missionsCompleted: '',
      portfolioPhotos: '',
      portfolioProjects: '',
      portfolioVideos: '',
    }
  });

  const formatTimeForDisplay = (time) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    return `${h}h${m !== '00' ? m : ''}`;
  };
  const buildScheduleString = () => {
    return DAYS.map(({ key, label }) => {
      const d = workSchedule[key];
      if (!d || !d.open) return `${label} : ${t('postRequest.closed')}`;
      return `${label} : ${formatTimeForDisplay(d.start)} - ${formatTimeForDisplay(d.end)}`;
    }).join('\n');
  };
  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  const filesToDataUrls = async (files) => {
    const list = Array.isArray(files) ? files : [];
    const out = [];
    for (const file of list) {
      try {
        out.push(await readFileAsDataUrl(file));
      } catch {
        // ignore single file read errors
      }
    }
    return out;
  };
  const updateWorkSchedule = (dayKey, field, value) => {
    setWorkSchedule((prev) => ({ ...prev, [dayKey]: { ...prev[dayKey], [field]: value } }));
  };
  const interventionRegionValue = watch('interventionRegion');
  const tradeValue = watch('trade');
  const selectedServiceForSubServices = services.find((s) => String(s._id || s.title) === String(tradeValue));
  const availableSubServices = selectedServiceForSubServices
    ? (Array.isArray(selectedServiceForSubServices.subServices) ? selectedServiceForSubServices.subServices : [])
    : [];

  useEffect(() => {
    const fetchServices = async () => {
      try {
        await api.initializeServices(false);
        const response = await api.getServices({});
        if (response.success) setServices(response.data || []);
        if (preSelectedTrade) setValue('trade', preSelectedTrade);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };
    fetchServices();
  }, [preSelectedTrade, setValue]);

  useEffect(() => {
    if (interventionRegionValue && tunisianRegions[interventionRegionValue]) {
      const sortedCities = [...tunisianRegions[interventionRegionValue]].sort();
      setAvailableInterventionCities(sortedCities);
      setSelectedInterventionRegion(interventionRegionValue);
      setValue('interventionCity', '');
    } else {
      setAvailableInterventionCities([]);
      setSelectedInterventionRegion('');
    }
  }, [interventionRegionValue, setValue]);

  useEffect(() => {
    setValue('subService', '');
  }, [tradeValue, setValue]);

  const onSubmit = async (data) => {
    setLoading(true);
    setShowError(false);
    setShowSuccess(false);
    setErrorMessage('');
    try {
      const scheduleStr = buildScheduleString();
      const notes = [scheduleStr, data.scheduleNotes ? String(data.scheduleNotes).trim() : ''].filter(Boolean).join('\n\n');
      const selectedService = services.find(s => (s._id || s.title) === data.trade);
      const photoUploads = await filesToDataUrls(portfolioPhotoFiles);
      const videoUploads = await filesToDataUrls(portfolioVideoFiles);
      const projectUploads = await filesToDataUrls(portfolioProjectFiles);
      const photoText = data.portfolioPhotos ? [String(data.portfolioPhotos).trim()] : [];
      const videoText = data.portfolioVideos ? [String(data.portfolioVideos).trim()] : [];
      const projectText = data.portfolioProjects ? [String(data.portfolioProjects).trim()] : [];
      const requestData = {
        userEmail: 'demande@nioland.local',
        services: data.trade ? [{ serviceId: data.trade, title: selectedService?.title || data.trade, tags: selectedService?.tags || [] }] : [],
        subService: data.subService || undefined,
        notes: notes || undefined,
        about: data.about || undefined,
        experience: data.experience || undefined,
        clientCommitments: data.clientCommitments || undefined,
        paymentMethod: data.paymentMethod || undefined,
        coverageGuarantees: data.coverageGuarantees || undefined,
        servicesOffered: data.servicesOffered || undefined,
        detailedSubServices: data.detailedSubServices || undefined,
        interventionCity: data.interventionCity || undefined,
        interventionRegion: data.interventionRegion || undefined,
        experienceYears: data.experienceYears || undefined,
        projectsCompleted: data.projectsCompleted || undefined,
        previousCompanies: data.previousCompanies || undefined,
        hourlyPrice: data.hourlyPrice || undefined,
        servicePrice: data.servicePrice || undefined,
        customQuote: data.customQuote || undefined,
        urgentAvailability: data.urgentAvailability || undefined,
        averageRating: data.averageRating || undefined,
        clientComments: data.clientComments || undefined,
        missionsCompleted: data.missionsCompleted || undefined,
        portfolioPhotos: [...photoText, ...photoUploads].filter(Boolean).length ? [...photoText, ...photoUploads].filter(Boolean) : undefined,
        portfolioProjects: [...projectText, ...projectUploads].filter(Boolean).length ? [...projectText, ...projectUploads].filter(Boolean) : undefined,
        portfolioVideos: [...videoText, ...videoUploads].filter(Boolean).length ? [...videoText, ...videoUploads].filter(Boolean) : undefined,
      };
      if (!data.trade || !requestData.services.length) {
        setErrorMessage(t('postRequest.tradeRequired'));
        setShowError(true);
        setLoading(false);
        return;
      }
      const response = await api.submitRequest(requestData);
      if (response.success) {
        setShowSuccess(true);
        setTimeout(() => navigate('/services'), 2000);
      } else {
        setErrorMessage(response.message || t('postRequest.submitFailed'));
        setShowError(true);
      }
    } catch (error) {
      setErrorMessage(error.message || t('postRequest.genericError'));
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const toasterMessages = [];
  if (showError) toasterMessages.push({ id: 'error', type: 'error', message: errorMessage });
  if (showSuccess) toasterMessages.push({ id: 'success', type: 'success', message: t('postRequest.successMsg') });

  return (
    <AppLayout title={t('postRequest.heading')} rootClass="layout-1">
      <NioToaster messages={toasterMessages} />
      <NioSection className="overflow-hidden pt-120 pt-lg-160" masks={['blur-1 left center']}>
        <NioSection.Content>
          <Row className="justify-content-center">
            <Col lg={10} xl={8}>
              <div className="text-center mb-5">
                <h2 className="mb-3">{t('postRequest.heading')}</h2>
                <p className="fs-18 text-muted">{t('postRequest.subheading')}</p>
              </div>

              {hasProviderParams ? (
                <div className="text-center mb-4">
                  <div className="text-muted small">
                    {t('postRequest.contacting')}
                    <strong className="ms-2">
                      {providerBusiness ? providerBusiness : providerName ? providerName : t('postRequest.theProvider')}
                    </strong>
                  </div>
                </div>
              ) : null}
              <Col md={12}>
                        <NioField label={t('postRequest.about')} htmlFor="about">
                          <NioField.Textarea
                            id="about"
                            rows={3}
                            placeholder={t('postRequest.aboutPlaceholder')}
                            errors={errors.about}
                            {...register('about')}
                          />
                        </NioField>
                      </Col> <br></br>
              <NioCard>
                <NioCard.Body className="p-4 p-md-5">
                  <Form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <div className="mb-4">
                      <input
                        type="hidden"
                        {...register('trade', { required: t('postRequest.tradeRequired') })}
                      />
                      <NioField label={t('postRequest.service')} htmlFor="trade">
                        {services.length > 0 ? (
                          <NioField.Select
                            id="trade"
                            value={tradeValue || ''}
                            errors={errors.trade}
                            onChange={(event) => setValue('trade', event.target.value, { shouldValidate: true })}
                          >
                            <option value="">{t('postRequest.selectService')}</option>
                            {services.map((service) => (
                              <option key={service._id || service.title} value={service._id || service.title}>
                                {service.titles?.[language] || service.title}
                              </option>
                            ))}
                          </NioField.Select>
                        ) : (
                          <p className="text-muted mb-0">{t('postRequest.loadingServices')}</p>
                        )}
                      </NioField>
                      {errors.trade && (
                        <small className="text-danger d-block mt-2">{errors.trade.message}</small>
                      )}
                    </div>
                    <div className="mb-4">
                      <NioField label={t('postRequest.subService')} htmlFor="subService">
                        <NioField.Select
                          id="subService"
                          errors={errors.subService}
                          disabled={!tradeValue || availableSubServices.length === 0}
                          {...register('subService')}
                        >
                          <option value="">{t('postRequest.selectSubService')}</option>
                          {availableSubServices.map((sub) => (
                            <option key={sub.key} value={sub.key}>{sub.labels?.fr || sub.labels?.en || sub.key}</option>
                          ))}
                        </NioField.Select>
                      </NioField>
                    </div>

                    <h5 className="mb-3 mt-4">{t('postRequest.availability')}</h5>
                    <div className="table-responsive mb-3">
                      <table className="table table-bordered align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t('postRequest.day')}</th>
                            <th className="text-center" style={{ width: '100px' }}>{t('postRequest.available')}</th>
                            <th>{t('postRequest.from')}</th>
                            <th>{t('postRequest.to')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {DAYS.map(({ key, label }) => (
                            <tr key={key}>
                              <td className="fw-medium">{label}</td>
                              <td className="text-center">
                                <Form.Check
                                  type="checkbox"
                                  checked={workSchedule[key]?.open || false}
                                  onChange={(e) => updateWorkSchedule(key, 'open', e.target.checked)}
                                />
                              </td>
                              <td>
                                <input
                                  type="time"
                                  className="form-control form-control-sm"
                                  value={workSchedule[key]?.start || '09:00'}
                                  onChange={(e) => updateWorkSchedule(key, 'start', e.target.value)}
                                  disabled={!workSchedule[key]?.open}
                                />
                              </td>
                              <td>
                              
                                <input
                                  type="time"
                                  className="form-control form-control-sm"
                                  value={workSchedule[key]?.end || '18:00'}
                                  onChange={(e) => updateWorkSchedule(key, 'end', e.target.value)}
                                  disabled={!workSchedule[key]?.open}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <small className="text-muted d-block mb-3">{t('postRequest.scheduleHint')}</small>

                    <NioField label={t('postRequest.scheduleNotes')} htmlFor="scheduleNotes">
                      <NioField.Textarea
                        id="scheduleNotes"
                        rows={3}
                        placeholder={t('postRequest.scheduleNotesPlaceholder')}
                        errors={errors.scheduleNotes}
                        {...register('scheduleNotes')}
                      />
                    </NioField>
                    <br></br>
                    <Row className="g-3">
                      <Col md={6}>
                        <NioField label={t('postRequest.interventionRegion')} htmlFor="interventionRegion">
                          <NioField.Select id="interventionRegion" errors={errors.interventionRegion} {...register('interventionRegion')}>
                            <option value="">{t('postRequest.selectRegion')}</option>
                            {sortedRegions.map((region) => (
                              <option key={region} value={region}>{region}</option>
                            ))}
                          </NioField.Select>
                        </NioField>
                      </Col>
                      <Col md={6}>
                        <NioField label={t('postRequest.interventionCity')} htmlFor="interventionCity">
                          <NioField.Select
                            id="interventionCity"
                            errors={errors.interventionCity}
                            disabled={!selectedInterventionRegion || availableInterventionCities.length === 0}
                            {...register('interventionCity')}
                          >
                            <option value="">{t('postRequest.selectCity')}</option>
                            {availableInterventionCities.map((city) => (
                              <option key={city} value={city}>{city}</option>
                            ))}
                          </NioField.Select>
                        </NioField>
                      </Col>

                      <Col md={12}>
                        <h6 className="mb-2 mt-2">{t('postRequest.experience')}</h6>
                      </Col>
                      <Col md={6}>
                        <NioField label={t('postRequest.yearsExp')} htmlFor="experienceYears">
                          <NioField.Select id="experienceYears" errors={errors.experienceYears} {...register('experienceYears')}>
                            <option value="">{t('postRequest.choose')}</option>
                            <option value="0-1">0-1 an</option>
                            <option value="2-5">2-5 ans</option>
                            <option value="6-10">6-10 ans</option>
                            <option value="11+">11+ ans</option>
                          </NioField.Select>
                        </NioField>
                      </Col>
                      <Col md={6}>
                        <NioField label={t('postRequest.projectsCompleted')} htmlFor="projectsCompleted">
                          <NioField.Input id="projectsCompleted" placeholder={t('postRequest.projectsPlaceholder')} errors={errors.projectsCompleted} {...register('projectsCompleted')} />
                        </NioField>
                      </Col>
                      <Col md={12}>
                        <NioField label={t('postRequest.previousCompanies')} htmlFor="previousCompanies">
                          <NioField.Textarea id="previousCompanies" rows={2} placeholder={t('postRequest.previousCompaniesPlaceholder')} errors={errors.previousCompanies} {...register('previousCompanies')} />
                        </NioField>
                      </Col>

                      <Col md={12}>
                        <h6 className="mb-2 mt-2">{t('postRequest.pricing')}</h6>
                      </Col>
                      <Col md={6}>
                        <NioField label={t('postRequest.hourlyPrice')} htmlFor="hourlyPrice">
                          <NioField.Input id="hourlyPrice" placeholder={t('postRequest.hourlyPricePlaceholder')} errors={errors.hourlyPrice} {...register('hourlyPrice')} />
                        </NioField>
                      </Col>
                      <Col md={6}>
                        <NioField label={t('postRequest.dailyPrice')} htmlFor="servicePrice">
                          <NioField.Input id="servicePrice" placeholder={t('postRequest.dailyPricePlaceholder')} errors={errors.servicePrice} {...register('servicePrice')} />
                        </NioField>
                      </Col>
                      <Col md={12}>
                        <NioField label={t('postRequest.customQuote')} htmlFor="customQuote">
                          <NioField.Textarea id="customQuote" rows={2} placeholder={t('postRequest.customQuotePlaceholder')} errors={errors.customQuote} {...register('customQuote')} />
                        </NioField>
                      </Col>
                      <Col md={12}>
                        <h6 className="mb-2 mt-2">{t('postRequest.portfolio')}</h6>
                      </Col>
                      <Col md={12}>
                        <NioField label={t('postRequest.photos')} htmlFor="portfolioPhotos">
                          <NioField.Textarea id="portfolioPhotos" rows={2} placeholder={t('postRequest.photosPlaceholder')} errors={errors.portfolioPhotos} {...register('portfolioPhotos')} />
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            webkitdirectory="true"
                            className="form-control mt-2"
                            onChange={(e) => setPortfolioPhotoFiles(e.target.files ? Array.from(e.target.files) : [])}
                          />
                          {portfolioPhotoFiles.length > 0 && (
                            <small className="text-muted d-block mt-1">
                              {t('postRequest.photosCount', { count: portfolioPhotoFiles.length })}
                            </small>
                          )}
                        </NioField>
                      </Col>
                      <Col md={12}>
                        <NioField label={t('postRequest.projectsPortfolio')} htmlFor="portfolioProjects">
                          <NioField.Textarea id="portfolioProjects" rows={2} placeholder={t('postRequest.projectsPortfolioPlaceholder')} errors={errors.portfolioProjects} {...register('portfolioProjects')} />
                          <input
                            type="file"
                            accept=".pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            multiple
                            webkitdirectory="true"
                            className="form-control mt-2"
                            onChange={(e) => setPortfolioProjectFiles(e.target.files ? Array.from(e.target.files) : [])}
                          />
                          {portfolioProjectFiles.length > 0 && (
                            <small className="text-muted d-block mt-1">
                              {t('postRequest.projectsCount', { count: portfolioProjectFiles.length })}
                            </small>
                          )}
                        </NioField>
                      </Col>
                      <Col md={12}>
                        <NioField label={t('postRequest.videos')} htmlFor="portfolioVideos">
                          <NioField.Textarea id="portfolioVideos" rows={2} placeholder={t('postRequest.videosPlaceholder')} errors={errors.portfolioVideos} {...register('portfolioVideos')} />
                          <input
                            type="file"
                            accept="video/*"
                            multiple
                            webkitdirectory="true"
                            className="form-control mt-2"
                            onChange={(e) => setPortfolioVideoFiles(e.target.files ? Array.from(e.target.files) : [])}
                          />
                          {portfolioVideoFiles.length > 0 && (
                            <small className="text-muted d-block mt-1">
                              {t('postRequest.videosCount', { count: portfolioVideoFiles.length })}
                            </small>
                          )}
                        </NioField>
                      </Col>

                      <Col md={12}>
                        <NioField label={t('postRequest.clientCommitments')} htmlFor="clientCommitments">
                          <NioField.Textarea
                            id="clientCommitments"
                            rows={3}
                            placeholder={t('postRequest.clientCommitmentsPlaceholder')}
                            errors={errors.clientCommitments}
                            {...register('clientCommitments')}
                          />
                        </NioField>
                      </Col>
                      <Col md={6}>
                        <NioField label={t('postRequest.paymentMethod')} htmlFor="paymentMethod">
                          <NioField.Select id="paymentMethod" errors={errors.paymentMethod} {...register('paymentMethod')}>
                            <option value="">{t('postRequest.choose')}</option>
                            <option value="Carte bancaire">{t('postRequest.paymentMethods.card')}</option>
                            <option value="Virement bancaire">{t('postRequest.paymentMethods.transfer')}</option>
                            <option value="Espèces">{t('postRequest.paymentMethods.cash')}</option>
                            <option value="Paiement en ligne">{t('postRequest.paymentMethods.online')}</option>
                            <option value="Chèque">{t('postRequest.paymentMethods.check')}</option>
                            <option value="Autre">{t('postRequest.paymentMethods.other')}</option>
                          </NioField.Select>
                        </NioField>
                      </Col>
                      <Col md={12}>
                        <NioField label={t('postRequest.coverageGuarantees')} htmlFor="coverageGuarantees">
                          <NioField.Textarea
                            id="coverageGuarantees"
                            rows={3}
                            placeholder={t('postRequest.coveragePlaceholder')}
                            errors={errors.coverageGuarantees}
                            {...register('coverageGuarantees')}
                          />
                        </NioField>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-between align-items-center mt-5">
                      <Link to="/services" className="text-muted">{t('postRequest.backToServices')}</Link>
                      <NioButton
                        type="submit"
                        className="btn-lg btn-primary"
                        label={loading ? t('postRequest.submitting') : t('postRequest.submit')}
                        disabled={loading}
                      />
                    </div>
                  </Form>
                </NioCard.Body>
              </NioCard>
            </Col>
          </Row>
        </NioSection.Content>
      </NioSection>
    </AppLayout>
  );
}

export default PostRequest;
